import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { getDisplayName, getColor } from '../../../../../../constants/skillLevels';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Title,
  CloseButton,
  ChartContainer,
} from './styles';

const SkillHistoryModal = ({ isOpen, onClose, skillData, skillLevels }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !skillData || !skillData.skillHistory || !chartRef.current || !skillLevels) return;

    // Clear previous chart
    d3.select(chartRef.current).selectAll('*').remove();

    // Sort history by date and filter out entries with null competencyLevel
    const sortedHistory = [...skillData.skillHistory]
      .filter(entry => entry.competencyLevel && entry.competencyLevel.name)
      .sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt));

    // Add current skill to history if it exists and is different from last history entry
    if (skillData.currentSkill && skillData.currentSkill.competencyLevel) {
      const lastHistoryEntry = sortedHistory[sortedHistory.length - 1];
      if (!lastHistoryEntry || 
          lastHistoryEntry.competencyLevelId !== skillData.currentSkill.competencyLevel.id || 
          lastHistoryEntry.updatedAt !== skillData.currentSkill.updatedAt) {
        sortedHistory.push(skillData.currentSkill);
      }
    }

    // Set up dimensions
    const margin = { top: 40, right: 60, bottom: 60, left: 160 };
    const width = chartRef.current.clientWidth - margin.left - margin.right;
    const height = chartRef.current.clientHeight - margin.top - margin.bottom;

    // Create SVG
    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create scales
    const xScale = d3.scaleTime()
      .domain(d3.extent(sortedHistory, d => new Date(d.updatedAt)))
      .range([0, width])
      .nice();

    // Create an array of level names sorted by their numeric level value
    const levelNames = [...skillLevels]
      .sort((a, b) => a.level - b.level)
      .map(level => level.displayName);
    
    const yScale = d3.scalePoint()
      .domain(levelNames)
      .range([height, 0])
      .padding(0.5);


    // Fix 3: Update line generator to use the correct y-position function
    const line = d3.line()
      .x(d => xScale(new Date(d.updatedAt)))
      .y(d => yScale(d.competencyLevel.name))
      .curve(d3.curveStepAfter);

    // Add axes
    svg.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale)
        .ticks(5)
        .tickFormat(d3.timeFormat('%b %d, %Y')))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)');

    // Add y-axis with colored labels
    const yAxis = svg.append('g')
      .call(d3.axisLeft(yScale));

    // Remove the default axis line
    yAxis.select('.domain').remove();
    // Remove the default tick lines
    yAxis.selectAll('.tick line').remove();

    // Helper function to get color for a level by displayName
    const getColorForLevel = (displayName) => {
      const level = skillLevels.find(l => l.displayName === displayName);
      return level?.color || '#888';
    };

    // Style the y-axis labels like skill level selector buttons
    yAxis.selectAll('.tick')
      .append('rect')
      .attr('x', -150) // Position the rect further left
      .attr('y', -10) // Center vertically
      .attr('width', 140) // Make the rect wider
      .attr('height', 20)
      .attr('rx', 4) // Rounded corners
      .style('fill', d => `${getColorForLevel(d)}10`)
      .style('stroke', d => getColorForLevel(d))
      .style('stroke-width', 1);

    // Add dots before text
    yAxis.selectAll('.tick')
      .append('circle')
      .attr('cx', -135) // Adjust dot position
      .attr('cy', 0)
      .attr('r', 4)
      .style('fill', d => getColorForLevel(d));

    // Style the text labels
    yAxis.selectAll('.tick text')
      .attr('x', -120) // Adjust text position
      .attr('dy', '.32em') // Center text vertically
      .style('font-size', '12px')
      .style('fill', '#333')
      .style('font-weight', '500')
      .style('text-anchor', 'start'); // Align text to the left after the dot

    // Add grid lines with skill level colors
    svg.selectAll('line.horizontal-grid')
      .data(levelNames)
      .enter()
      .append('line')
      .attr('class', 'horizontal-grid')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', d => yScale(d))
      .attr('y2', d => yScale(d))
      .style('stroke', d => `${getColorForLevel(d)}30`)
      .style('stroke-width', 1);

    // Add the line path with gradient based on skill levels
    const lineGradient = svg.append('linearGradient')
      .attr('id', 'skill-line-gradient')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', 0)
      .attr('y1', height)
      .attr('x2', 0)
      .attr('y2', 0);

    skillLevels.forEach((level, i) => {
      lineGradient.append('stop')
        .attr('offset', `${(i / (skillLevels.length - 1)) * 100}%`)
        .attr('stop-color', level.color);
    });

    // Fix 4: Make sure we have at least 2 data points for the line
    if (sortedHistory.length >= 2) {
      // Add the line path
      svg.append('path')
        .datum(sortedHistory)
        .attr('fill', 'none')
        .attr('stroke', 'url(#skill-line-gradient)')
        .attr('stroke-width', 2)
        .attr('d', line);
    }

    // Fix 5: Update dots to use the correct y-position function
    svg.selectAll('.dot')
      .data(sortedHistory)
      .enter()
      .append('circle')
      .attr('class', 'dot')
      .attr('cx', d => xScale(new Date(d.updatedAt)))
      .attr('cy', d => yScale(d.competencyLevel.name))
      .attr('r', (d, i) => i === sortedHistory.length - 1 ? 8 : 5) // Make last dot larger
      .style('fill', d => getColor(d.competencyLevel.id, skillLevels))
      .style('stroke', '#fff')
      .style('stroke-width', (d, i) => i === sortedHistory.length - 1 ? 3 : 2); // Thicker stroke for last dot

    // Add tooltips
    const tooltip = d3.select(chartRef.current)
      .append('div')
      .attr('class', 'tooltip')
      .style('position', 'absolute')
      .style('background-color', 'white')
      .style('padding', '8px')
      .style('border-radius', '4px')
      .style('box-shadow', '0 2px 4px rgba(0,0,0,0.1)')
      .style('pointer-events', 'none')
      .style('opacity', 0);

    svg.selectAll('.dot')
      .on('mouseover', (event, d) => {
        const competencyLevelId = d.competencyLevel.id;
        tooltip.transition()
          .duration(200)
          .style('opacity', .9);
        tooltip.html(
          `<div style="color: ${getColor(competencyLevelId, skillLevels)}">
            <strong>${getDisplayName(competencyLevelId, skillLevels)}</strong>
           </div>
           ${new Date(d.updatedAt).toLocaleDateString()}<br/>
           Updated by: ${d.updatedBy}`
        )
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 28) + 'px');
      })
      .on('mouseout', () => {
        tooltip.transition()
          .duration(500)
          .style('opacity', 0);
      });

  }, [isOpen, skillData, skillLevels]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <Title>
            <span>{skillData.name}</span>
          </Title>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        <ChartContainer ref={chartRef} />
      </ModalContent>
    </ModalOverlay>
  );
};

export default SkillHistoryModal; 